import React, {createContext, useContext, useEffect, useState} from 'react';
import {useFieldArray, useForm, useWatch} from 'react-hook-form';
import {useNavigate, useParams} from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/client';
import useGetDicti from '../../../common/hooks/useGetDicti';
import {
    DictKeyEnum, LoadAgreementQuery,
    PrecalcMutation, PrecalcMutationVariables,
    PrintExpressCalculationMutation,
    PrintExpressCalculationMutationVariables
} from '../../../../../libs/api/graphqlTypes';
import {IForm, IFormField, TypeFormHook} from '../../../../../libs/uiKit/fieldsBuilder/types';
import { ITabItem } from '../../../../../libs/uiKit/tabs/types';
import { ISelectItem } from '../../../../../libs/uiKit/select/types';
import { IEnumItem } from '../../../../../libs/uiKit/enums/types';
import useNotification from '../../../../widgets/notifier/ui/hooks/useNitification';
import { stringToMoney } from '../../../../../libs/uiKit/utils';
import {
    BANKS_REQUIRED_CREDIT_DATE, PRECALC_MATERIAL_SHOW_FOR, PRECALC_PROPERTY_HIDE_MATERIAL_FOR,
    PRODUCT_FLAT, PROPERTY_BATHHOUSE,
    PROPERTY_GARAGE,
    PROPERTY_GUEST_HOUSE, PROPERTY_HIDE_WOOD_ELEMENTS_FOR, PROPERTY_HIDE_YEAR_FOR,
    PROPERTY_HOUSE,
    PROPERTY_LAND, PROPERTY_MATERIAL_SHOW_FOR, PROPERTY_PARKING,
    PROPERTY_TOWNHOUSE,
    PROPERTY_TYPE,
    RADIO_BOOLEAN,
    RADIO_SEX,
    RADIO_TITLE,
    RISKS_SELECT
} from '../../../../../libs/utils/staticData';
import { useGlobalContext } from '../../../../../apps/main/ui/GlobalContext';
import { useUserRights } from '../../../common/hooks/useUserRights';
import usePrecalculationCommands from '../../ds/repositories/commands';
import { DOWNLOAD_PRECALC } from '../../../../../libs/api/commands';
import { fileDownloader } from '../../../../../libs/utils/fileDownloader';
import {IDaDataValue} from "../../../../../libs/uiKit/daData/types";
import Button from "../../../../../libs/uiKit/button";
import {TrashIcon} from "../../../../../icons/ui/Trash";
import {RedTrashIcon} from "../../../../../icons/ui/RedTrash";
import {isNewRegion} from "../../../../../libs/utils/address";
import {PRECALCULATION} from "../../../../../libs/api/queries";
import {
    formatAddressToAddressForm,
    formatAgreementDataToFormData,
    setDataToForm
} from "../../../createAgreement/ds/repositories/commands";
import {AgreementId, CreateAgreementForm} from "../../../createAgreement/ui/context";
import {QueriesCreateAgreement} from "../../../createAgreement/ds/repositories/queries";
import {getClearSelectValue} from "../../../../../libs/utils/getSelectdefaultValue";
import {formatStringRuToDate} from "../../../../../libs/utils/gormatDateToUsa";

type PrecalculationContext = {
	goBackHandler: () => void;
	onFormSubmit: () => void;
	isShowRiskLife: boolean;
	isShowRiskProperty: boolean;
	isShowRiskTitle: boolean;
	isLoading: boolean;
	isDisabled: boolean;
	precalculationForm: IForm;
    precalculationFormData: PrecalculationForm | null;
	borrowerForm: IForm;
	risksForm: IForm;
	propertyItems: PropertyItem[];
    bankItems: BankItem[];
	titleForm: IForm;
	borrowersItems: BorrowerItem[];
	downloadAllHandler: () => void;
	isLoadingResults: boolean;
	changeSelectedPrecalcHandler: (isn: string) => void;
	selectBankHandler: (isn: string, expressMode: boolean, crossId: number | null | undefined) => void;
	resultItems: ResultItem[];
	isDisabledSelectBank: boolean;
	propAppend: (value: any) => void;
	propRemove: (value: any) => void;
    bankAppend: (value: any) => void;
    bankRemove: (value: any) => void;
    append: (value: any) => void;
    remove: (value: any) => void;
	formHook: TypeFormHook;
	propsChangeRnd: number;
    isDataChanged: boolean;
    recalcForm: (formData: PrecalculationForm) => void;
    keepOnlyOneBank: (bankIsn: string | null) => void;
    discountKv: number|"";
    setDiscountKv: (kv: number|"") => void;
    discountSk: number|"";
    setDiscountSk: (sk: number|"") => void;
    promocode: string;
    setPromocode: (code: string) => void;
    fixedDiscount: number;
    stateContinueType: 'anketa' | 'declaration'|null;
    setStateContinueType: (value: 'anketa' | 'declaration'|null) => void;
};

type BorrowerItem = { id: string; form: IForm };
type PropertyItem = { id: string; form: IForm, title: string|undefined };
type BankItem = { id: string; form: IForm };

export type PersonField = {
	birthDateMethod?: ISelectItem;
	id?: string;
	birthDate?: Date;
	sex?: string;
	share?: number;
	isChronical?: boolean;
	riskGroup?: ISelectItem;
	profession?: ISelectItem;
    hasRisk?: boolean;
};

type BorrowerResultItem = {
	name: string | undefined;
	tariff: number;
	premium: number;
	needOms: boolean;
};

export type RiskResultItem = {
	name: string | undefined;
	isTableForLife: boolean;
	borrowers: BorrowerResultItem[];
    sum: number;
};

type ResultItem = {
	isSelected: boolean;
	isn: string;
	isnBank: string;
	name: string;
	sum: string;
	premium: string;
	logo: string;
	risks: RiskResultItem[];
	isExpress: boolean;
	noExpressReason?: string;
	crossId?: number | null | undefined;
    warning: string | null | undefined;
    oldPremium: string | null | undefined;
    allowDiscount: boolean;
    resultDiscount: number;
};

export type PropertyField = {
	objectType?: ISelectItem;
	objectBuildYear?: string;
	woodInConstruction?: boolean;
	pledgeShare?: number;
	wallMaterial?: ISelectItem;
	floorMaterial?: ISelectItem;
	subjectCost?: number;
}

export type BankField = {
    bank?: ISelectItem;
    rate?: number;
}

export type PrecalculationForm = {
	borrowers?: PersonField[];
	haveCoBorrower?: boolean;
	life?: boolean;
	property?: boolean;
	title?: boolean;
	ownershipYears?: string;
	summ?: number;
	banks?: BankField[];
	creditIssueDate?: Date|null;
	dealCountry?: IDaDataValue | null;
	agentIsn?: ISelectItem;
	insuranceType?: ISelectItem;
	promocode?: string;
	properties?: PropertyField[];
	crossInterior?: ISelectItem;
	crossResponsibility?: ISelectItem;
	crossProperty?: ISelectItem;
	crossRisks?: boolean;
};

const INSURANCE_TYPES = [
    { value: "1", label: "Новый кредит"},
    { value: "2", label: "Дострахование рисков"},
    { value: "3", label: "Рефинансирование"},
    { value: "4", label: "Смена страховой компании"},
    { value: "5", label: "Перезаключение договора"},
]

const PrecalculationContext = createContext<PrecalculationContext>({} as PrecalculationContext);
export const usePrecalculationContext = (): PrecalculationContext => useContext(PrecalculationContext);

const PrecalculationContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const {
        api,
		user: { isStaffMember,
            restrictions: {
                maxDiscount
            }
        },
		routes: { createAgreementStep, declarationById },
	} = useGlobalContext();
	const navigate = useNavigate();
	const { setNotification } = useNotification();
	const { rightsHandler } = useUserRights();
	const { createAgreementCmd, isLoadingSaveAgreement } = usePrecalculationCommands();
	const { data: selectBanks, loading: loadingBanks } = useGetDicti(DictKeyEnum.CreditBank);
	//const { data: selectBankPrograms, loading: loadingBankPrograms } = useGetDicti(DictKeyEnum.CreditBankProgram);
	const { data: selectPledge, loading: loadingPledge } = useGetDicti(DictKeyEnum.PropertyType);
	const { data: selectRiskGroup } = useGetDicti(DictKeyEnum.RiskGroup);
	const { data: selectProfession } = useGetDicti(DictKeyEnum.Profession);
	const { data: selectWallMaterial, loading: loadingWallMaterial } = useGetDicti(DictKeyEnum.WallMaterial);
	const { data: selectFloorMaterial, loading: loadingFloorMaterial } = useGetDicti(DictKeyEnum.FloorMaterial);

    const [precalculation, { data: dataPrecalculation, loading: loadingPrecalculation }] = useMutation<PrecalcMutation, PrecalcMutationVariables>(PRECALCULATION);

	const [stateSelectedPrecalc, setSelectedPrecalc] = React.useState<string|null>(null);
	const [stateTabs, setTab] = React.useState<ITabItem | undefined>(PROPERTY_TYPE[0]);
	const [statePrecalculationFormData, setPrecalculationFormData] = React.useState<PrecalculationForm | null>(null);
    const [stateFixedDiscountKv, setFixedDiscountKv] = React.useState<number|"">(0);
    const [stateFixedDiscountSk, setFixedDiscountSk] = React.useState<number|"">(0);
    const [stateFixedPromocode, setFixedPromocode] = React.useState<string>("");
    const [isDataChanged, setIsDataChanged] = React.useState(false);
    const [stateResultItems, setResultItems] = React.useState<ResultItem[]>([]);
    const [stateDiscountKv, setDiscountKv] = React.useState<number|"">("");
    const [stateDiscountSk, setDiscountSk] = React.useState<number|"">("");
    const [statePromocode, setPromocode] = React.useState<string>("");
    const [stateDiscountBank, setDiscountBank] = React.useState<string|undefined>(undefined);
    const [borrowersWatch, setBorrowersWatch] = React.useState<PersonField[]>([]);
    const [touthRnd, setTouthRnd] = React.useState<number>(0);
    const [possibleRisk, setPossibleRisk] = React.useState<boolean>(false);
    const [lastRiskBirthday, setLastRiskBirthday] = React.useState<Date|undefined>(undefined);
    const [lastRiskSex, setLastRiskSex] = React.useState<string|undefined>(undefined);

    const [stateContinueType, setStateContinueType] = React.useState<'anketa' | 'declaration'|null>(null);


	const [propsChangeRnd, setPropsChangeRnd] = useState<number>(0);

    const [stateBanksCount, setBanksCount] = useState<number>(1);

	const { data: agentsItems, loading: loadingAgents } = useGetDicti(DictKeyEnum.Agentlist);
	const { data: insureTypes, loading: loadingInsureTypes} = useGetDicti(DictKeyEnum.AgreementTypes);

	const [downloadPrecalc, { loading: loadingDownloadPrecalc }] = useMutation<PrintExpressCalculationMutation, PrintExpressCalculationMutationVariables>(DOWNLOAD_PRECALC);

	const formHook = useForm<PrecalculationForm>({
		mode: 'onBlur'
	});
	const { fields, append, remove } = useFieldArray({
		control: formHook.control,
		name: 'borrowers',
	});

	const { fields: propFields, append: propAppend, remove: propRemove } = useFieldArray({
		control: formHook.control,
		name: 'properties',
	});

    const {fields: bankFields, append: bankAppend, remove: bankRemove} = useFieldArray({
        control: formHook.control,
        name: 'banks',
    });

    const formValues = useWatch({ control: formHook.control });

    const { id } = useParams<{ id?: string }>();
    const agreementId: AgreementId = Number.isNaN(Number(id)) ? null : Number(id);

    const queriesCreateAgreements = React.useMemo(() => QueriesCreateAgreement(api), [api]);

    const formatBackToPrecalcForm = (agreementData: LoadAgreementQuery): PrecalculationForm => {
        const calcForm = agreementData.loadAgreement?.calcForm;

        const {riskLife, riskProperty, riskTitle, credit, propertyObjects, policyholder, borrower, coBorrowers} = calcForm || {};

        return {
            life: riskLife || false,
            property: riskProperty || false,
            title: riskTitle || false,
            insuranceType: INSURANCE_TYPES.find(type => type.value === String(credit?.insuranceType)) || undefined,
            agentIsn: getClearSelectValue(credit?.agent) || undefined,
            summ: credit?.creditSum || undefined,
            creditIssueDate: credit?.creditIssueDate ? formatStringRuToDate(credit.creditIssueDate) : null,
            dealCountry: formatAddressToAddressForm(credit?.city) || undefined,
            banks: [{
                bank: getClearSelectValue(credit?.bank) || undefined,
                rate: credit?.creditRate || 0
            }],
            haveCoBorrower: (coBorrowers && coBorrowers.length > 0) || false,
            borrowers: [
                {
                    birthDate: borrower?.birthday ? formatStringRuToDate(borrower?.birthday) : undefined,
                    share: borrower?.share || undefined,
                    sex: RADIO_SEX.find(sex => sex.value === borrower?.sex)?.value || undefined,
                    //hasRisk: borrower.hasRisk,
                    riskGroup: getClearSelectValue(borrower?.employment?.riskGroup) || undefined,
                    profession: getClearSelectValue(borrower?.employment?.profession) || undefined,
                },
                ...(coBorrowers ? coBorrowers.map(coBorrower => ({
                    birthDate: coBorrower?.birthday ? formatStringRuToDate(coBorrower?.birthday) : undefined,
                    share: coBorrower?.share || undefined,
                    sex: RADIO_SEX.find(sex => sex.value === coBorrower?.sex)?.value || undefined,
                    //hasRisk: !!(coBorrower?.employment?.riskGroup || coBorrower?.employment?.profession),
                    riskGroup: getClearSelectValue(coBorrower?.employment?.riskGroup) || undefined,
                    profession: getClearSelectValue(coBorrower?.employment?.profession) || undefined,
                })) : []),
            ],
            properties: (propertyObjects || []).map(property => ({
                objectType: getClearSelectValue(property?.propertyPledge) || undefined,
                objectBuildYear: String(property?.buildYear) || '',
                floorMaterial: getClearSelectValue(property?.floorMaterial) || undefined,
                wallMaterial: getClearSelectValue(property?.wallMaterial) || undefined,
                woodInConstruction: property?.woodInConstruction || false,
            })),
        }
    }

    const touchWatch = () => {
        setTouthRnd(Math.random());
    };

    const setDataToForm = (data: PrecalculationForm, formHook: TypeFormHook) => {
        Object.entries(data).forEach(([key, value]) => {
            if (value === null || value === undefined) return;
            formHook.setValue(key, value);
            console.log('set form value', key, value);
        });
        formHook.setValue('banks[0].rate', data.banks?.[0]?.rate || 0);
        formHook.setValue('borrowers[0].hasRisk', data.borrowers?.[0]?.hasRisk || false);
        if(data.properties?.[0]?.objectBuildYear){
            formHook.setValue('properties[0].objectBuildYear', data.properties?.[0]?.objectBuildYear);
        }
        touchWatch();
    };

    const refreshFormDataHandler = (callback?: () => void, force?: boolean) => {
        if (!agreementId) return;
        queriesCreateAgreements
            .getAgreementById(agreementId, force)
            .then(({ data }) => {
                if (!data) return setNotification({ type: 'error', text: 'no agreement data' });
                const formData = formatBackToPrecalcForm(data);
                setDataToForm(formData, formHook);
                const resultItem = data.loadAgreement?.calcForm?.precalcResult || null;
                if(resultItem){
                    setResultItems([ resultItem ]);
                    if(resultItem?.discountSk){
                        setDiscountSk(resultItem?.discountSk);
                    }
                    if(resultItem?.discount){
                        setDiscountKv(resultItem?.discount);
                    }
                }
                setPrecalculationFormData(JSON.parse(JSON.stringify(formData)));
                setTimeout(() => {
                    setIsDataChanged(false);
                    setStateContinueType("declaration");
                },1);

                /*setStateCalcForm(data?.loadAgreement?.calcForm);
                console.log('calc form loaded', data?.loadAgreement?.calcForm);
                setStatePrecalcResult(data?.loadAgreement?.calcForm?.precalcResult);
                if(callback !== undefined){
                    callback();
                }*/
            })
            .catch(error => setNotification({ type: 'error', text: error }));
    }

    React.useEffect(() => {
        refreshFormDataHandler();
        window.scrollTo({
            left: 0,
            top: 0,
            behavior: 'smooth',
        });

    }, [agreementId]);

    React.useEffect(() => {
        if (statePrecalculationFormData) {
            const realFormValues = formHook.getValues();
            const changed = JSON.stringify(realFormValues) !== JSON.stringify(statePrecalculationFormData)
                || stateDiscountKv !== stateFixedDiscountKv
                || statePromocode !== stateFixedPromocode
                || stateDiscountSk !== stateFixedDiscountSk;
            setIsDataChanged(changed);
            if(changed){
                //console.log('CHANGED', realFormValues, statePrecalculationFormData, stateDiscountKv, stateFixedDiscountKv, statePromocode, stateFixedPromocode);
            }
        }
    }, [formValues, statePrecalculationFormData, propsChangeRnd, stateDiscountKv, statePromocode, stateDiscountSk]);

	const [banks, riskLife, riskProperty, riskTitle, properties] = formHook.watch(['banks','life', 'property', 'title', 'properties']);

    // use effect, set borrowersWatch, if touch touthRnd
    React.useEffect(() => {
        const currentBorrowers = formHook.getValues('borrowers') || [];
        setBorrowersWatch(currentBorrowers);
        const hasRisk = currentBorrowers.some((borrower) => borrower.hasRisk || borrower.isChronical);
        if(hasRisk){
            setPossibleRisk(true);
            setLastRiskBirthday(currentBorrowers[0].birthDate);
            setLastRiskSex(currentBorrowers[0].sex);
            console.log('Detected possible risk');
        }
    }, [touthRnd]);

	const isDisabledPrecalculation: boolean = !(riskLife || riskProperty || riskTitle);



	const downloadAllHandler = React.useCallback(() => {
		if (!stateResultItems.length) return;
		downloadPrecalc({
			variables: {
				isn: stateResultItems.map(({ isn }) => isn),
			},
		})
			.then(({ data }) => {
				if (!data?.printExpressCalculation) return setNotification({ type: 'error', text: 'no doc' });
				fileDownloader(data.printExpressCalculation.url, data.printExpressCalculation.filename);
			})
			.catch(error => setNotification({ type: 'error', text: error }));
	}, [downloadPrecalc, stateResultItems, setNotification]);

	const changeSelectedPrecalcHandler = (currentIsn: string) => {
        setSelectedPrecalc(currentIsn);
    };

	const selectBankHandler = React.useCallback(
		(bankIsn: string, expressMode: boolean, crossId: number | null | undefined) => {
			if (!statePrecalculationFormData) return setNotification({ type: 'error', text: 'no form data' });
			createAgreementCmd({
				bank: selectBanks.find(bank => bank.value === bankIsn),
				tabValue: stateTabs,
				formData: statePrecalculationFormData,
				callback: isn => navigate(expressMode ? declarationById(isn) : createAgreementStep(isn, 'contragentStep')),
				expressMode,
				crossId,
                promocode: statePromocode,
                discount: stateDiscountKv ? Number(stateDiscountKv) : 0,
                discountSk: stateDiscountSk ? Number(stateDiscountSk) : 0,
                resultItem: stateResultItems?.[0] || null,
                id: agreementId || undefined,
			});
		},
		[createAgreementCmd, createAgreementStep, navigate, selectBanks, setNotification, statePrecalculationFormData, stateTabs]
	);

	const changeHaveCoBorrowerHandler = (ev: React.ChangeEvent<HTMLInputElement>) => {
			if (ev.target.checked) append({ share: 0 });
			if (!ev.target.checked) {
                //remove all co-borrowers except first
                console.log('REMOVE ALL EXCEPT FIRST', fields.length);
                for(let i = fields.length - 1; i > 0; i--){
                    remove(i);
                }
            }
		}

	const changeRisksHandler = React.useCallback(
		(checked: boolean, risk: string | number): void => {
			if (!checked && risk === 'property') formHook.setValue('title', false);
		},
		[formHook]
	);

    const recalcForm = (formData: PrecalculationForm) => {

        if(stateDiscountSk !== "" && statePromocode !== ""){
            return setNotification({type: "error", text: "Нельзя одновременно применять промокод и скидку за счет Компании"});
        }
        if(stateDiscountSk !== "" && maxDiscount && stateDiscountSk > maxDiscount){
            return setNotification({type: "error", text: "Максимальная скидка за счет Компании: " + maxDiscount + '%'});
        }

        if(formData.crossRisks && !formData.crossProperty && !formData.crossInterior && !formData.crossResponsibility){
            formHook.setError('crossInterior', { type: 'manual', message: ' ' });
            formHook.setError('crossResponsibility', { type: 'manual', message: ' ' });
            formHook.setError('crossProperty', { type: 'manual', message: 'Необходимо выбрать хотя бы один риск' });
            return;
        }

        const { summ, banks, haveCoBorrower, borrowers = [], ownershipYears, life, property, title, creditIssueDate, dealCountry, agentIsn, insuranceType, properties } = formData;

        if(!!dealCountry && dealCountry?.data?.city){
            if(isNewRegion(dealCountry)){
                return setNotification({type: "error", text: "Расчет по выбранным параметрам невозможен. Обратитесь к куратору!"});
            }
        }

        let maxShare = 100;
        if(borrowers[0]?.share && Number(borrowers[0].share) === 100){
            maxShare = 200;
        }

        const totalShare: number = borrowers.reduce((acc, borrower) => {
            if (borrower?.share) {
                return acc + Number(borrower?.share || 0);
            }
            return acc;
        }, 0);
        if (totalShare > maxShare) {
            return setNotification({ type: 'error', text: `Суммарная доля заёмщиков не может превышать ${maxShare}%` });
        }
        if(borrowers.length > 1 && maxShare === 100 && totalShare < 100){
            return setNotification({ type: 'error', text: `Суммарная доля заёмщиков должна быть 100%` });
        }
        if(borrowers.length > 1 && totalShare === 0){
            return setNotification({ type: 'error', text: 'Суммарная доля заёмщиков не может быть 0' });
        }
        const allSharesAboveZero = borrowers.every(borrower => borrower?.share && Number(borrower.share) > 0);
        if (borrowers.length > 1 && !allSharesAboveZero) {
            return setNotification({ type: 'error', text: 'Доля созаемщика не может быть 0' });
        }

        if(properties && properties?.length > 1){
            const totalPropertyShare: number = properties.reduce((acc, property) => {
                if (property?.pledgeShare) {
                    return acc + Number(property?.pledgeShare || 0);
                }
                return acc;
            }, 0);
            if(totalPropertyShare > 100){
                return setNotification({ type: 'error', text: "Сумма значений атрибута Доля в страховании, % по имуществу должна быть не более 100%" });
            }
            if(totalPropertyShare < 100){
                return setNotification({ type: 'error', text: "Сумма значений атрибута Доля в страховании, % по имуществу должна быть не менее 100%" });
            }
        }

        const hasPosibleRisk = possibleRisk
            && borrowers[0]?.birthDate === lastRiskBirthday
            && borrowers[0]?.sex === lastRiskSex;

        const risks = {
            riskLife: life,
            riskTitle: title,
            riskProperty: property,
        };

        const propertiesWithIncorrectWood = (properties || []).filter(
            property => property?.woodInConstruction
                && property?.wallMaterial?.label?.includes('Железобетон')
                && property?.floorMaterial?.label?.includes('Железобетон')
        ).length > 0;
        if(propertiesWithIncorrectWood){
            return setNotification({ type: 'error', text: 'Если выбран чек-бокс "В стенах/перекрытиях есть дерево", то нельзя выбрать одновременно "Железобетон/Кирпич/Камень" и "Железобетон"' });
        }

        const cancelDiscount = banks?.[0]?.bank?.value !== stateDiscountBank;

        if(cancelDiscount){
            console.log('bank changed, cancel discount', stateDiscountBank, banks?.[0]?.bank?.value);
            setDiscountKv("");
            setPromocode("");
            setFixedDiscountKv("");
            setFixedDiscountSk("");
            setFixedPromocode("");
        }

        precalculation({
            variables: {
                precalcForm: {
                    ...risks,
                    insuranceType,
                    creditSum: summ,
                    creditIssueDate: creditIssueDate ? dayjs(creditIssueDate).format('DD.MM.YYYY') : null,
                    banks: (banks || []).map(bank => ({
                        isn: bank?.bank?.value,
                        rate: bank?.rate,
                    })),
                    borrowers: borrowers.map((borrower) => ({
                        birthday: borrower?.birthDate ? dayjs(borrower?.birthDate).format('DD.MM.YYYY') : null,
                        sex: borrower?.sex,
                        share: borrowers.length === 1 ? 100 : Number(borrower?.share) || null,
                        hasChronicDiseases: borrower?.isChronical,
                        hasRisk: borrower?.hasRisk || hasPosibleRisk,
                        //riskGroup: borrower?.hasRisk ? borrower?.riskGroup : null,
                        //profession: borrower?.hasRisk ? borrower?.profession : null,
                        birthdayMethod: borrower?.birthDateMethod
                    })),
                    dealCountry,
                    agent: agentIsn,
                    promocode: cancelDiscount ? "" : statePromocode,
                    properties: properties?.map(property => ({
                        objectType: property?.objectType,
                        buildYear: Number(property?.objectBuildYear) || null,
                        //ownershipYears: Number(property?.ownershipYears) || null,
                        woodInConstruction: property?.woodInConstruction,
                        floorMaterial: property?.floorMaterial,
                        wallMaterial: property?.wallMaterial,
                        pledgeShare: Number(property?.pledgeShare) || null,
                        subjectCost: Number(property?.subjectCost) || null,
                    })),
                    crossInterior: (formData.crossRisks && formData.crossInterior) ? parseInt(formData.crossInterior.value, 10) : null,
                    crossResponsibility: (formData.crossRisks && formData.crossResponsibility) ? parseInt(formData.crossResponsibility.value, 10) : null,
                    crossProperty: (formData.crossRisks && formData.crossProperty) ? parseInt(formData.crossProperty.value, 10) : null,
                    discountKv: cancelDiscount ? 0 : (stateDiscountKv === "" ? 0 : stateDiscountKv),
                    discountSk: stateDiscountSk === "" ? 0 : stateDiscountSk,
                },
            },
        })
            .then((data) => {

                const insuranceType = data.data?.precalc?.[0]?.insuranceType;

                setPrecalculationFormData(JSON.parse(JSON.stringify(formData)));
                if(!cancelDiscount){
                    setFixedDiscountKv(stateDiscountKv);
                    setFixedDiscountSk(stateDiscountSk)
                    setFixedPromocode(statePromocode);
                }
                setIsDataChanged(false);
                setSelectedPrecalc(null);
                setStateContinueType(null);
                setDiscountBank(banks?.[0]?.bank?.value);

                if(insuranceType === 1){
                    formHook.setValue('insuranceType', {
                        label: 'Новый кредит',
                        value: '1'
                    });
                    setPrecalculationFormData(JSON.parse(JSON.stringify(formHook.getValues())));
                }
                if(insuranceType === 4){
                    formHook.setValue('insuranceType', {
                        label: 'Смена страховой компании',
                        value: '4'
                    });
                    setPrecalculationFormData(JSON.parse(JSON.stringify(formHook.getValues())));
                }


                if(insuranceType === 1){
                    formHook.setValue('insuranceType', {
                        label: 'Новый кредит',
                        value: '1'
                    });
                }

                const resultItems: ResultItem[] = (data?.data?.precalc || []).reduce<ResultItem[]>((prev, item) => {
                    if (!item || !item.bank || !item.isn) return prev;
                    return [
                        ...prev,
                        {
                            isSelected: false,
                            isn: item.isn,
                            isnBank: item.bank.isn,
                            name: item.bank.name,
                            sum: `${stringToMoney(item.sum, 0)} ₽`,
                            premium: `${stringToMoney(item.total)} ₽`,
                            logo: item.bank.icon || '',
                            risks: (item.risks || []).map(risk => ({
                                name: risk?.name || undefined,
                                isTableForLife: risk?.name === 'Жизнь',
                                borrowers: (risk?.borrowers || []).map((borrower, index) => ({
                                    name: borrower?.name || undefined,
                                    tariff: borrower?.tariff || 0,
                                    premium: borrower?.premium_sum || 0,
                                    needOms: !!borrower?.need_medical_checkup || false,
                                })),
                                sum: risk?.sum || 0,
                            })),
                            isExpress: !!item.isExpressDeclarationAvailable,
                            noExpressReason: item.noExpressDeclarationReason || "",
                            crossId: item.crossId,
                            warning: item.warning,
                            oldPremium: item.oldPremium ? `${stringToMoney(item.oldPremium)} ₽` : undefined,
                            allowDiscount: item.allowDiscount || false,
                            resultDiscount: item.result_discount || 0,
                        },
                    ];
                }, []);
                setResultItems(resultItems);

                window.scrollTo({
                    left: 0,
                    top: 0,
                    behavior: "smooth"
                });
            })
            .catch(error => setNotification({ type: 'error', text: error }));
    };

	const onFormSubmit = formHook.handleSubmit(formData => {
        recalcForm(formData);
	});

	React.useEffect(() => {
		remove();
		append({ share: 100 });
		propRemove();
		propAppend({});
        bankAppend({});
		formHook.setValue('haveCoBorrower', false);
	}, [append, formHook, remove, stateTabs?.value]);

    React.useEffect(() => {
        if(riskProperty && propFields.length === 0){
            propAppend({});
        }
    }, [riskProperty, propFields]);

    //console.log('propFields', propFields);

	const borrowersItems: BorrowerItem[] = fields.map(({ id }, index) => {
		const defaultValues: PersonField = fields[index];
		const personLabel = (index === 0) ? 'заемщика' : 'созаемщика';
		return {
			id,
			form: {
				formHook,
				fields: [
					{
						field: {
							fieldType: 'date',
							fieldName: `borrowers[${index}].birthDate`,
							isRequired: true,
							placeholder: `Дата рождения ${personLabel}`,
							errorMessage: 'Выберите дату рождения',
							defaultValue: defaultValues.birthDate,
                            grid: 6,
						},
					},
                    {
                        label: `Пол ${personLabel}`,
                        field: {
                            fieldType: 'radio',
                            fieldName: `borrowers[${index}].sex`,
                            defaultValue: defaultValues.sex || RADIO_SEX[0].value,
                            items: RADIO_SEX,
                            grid: 6,
                        },
                    },
					{
						isHidden: !rightsHandler('unifiedRatesCalculation'),
						field: {
							fieldType: 'select',
							fieldName: `borrowers[${index}].birthDateMethod`,
							placeholder: `Расчет возраста ${personLabel}`,
							defaultValue: {label: "Автоматически", value: "auto"},
							items: [
								{label: "Автоматически", value: "auto"},
								{label: "По году рождения", value: "0"},
								{label: "По дате рождения", value: "1"},
							],
                            grid: 6,
						},
					},
					{
                        isHidden: fields.length < 2,
						field: {
							fieldType: 'range',
							fieldName: `borrowers[${index}].share`,
							isRequired: true,
							placeholder: `Доля в страховании, %`,
							defaultValue: defaultValues.share,
							min: 0,
							max: 100,
							step: 1,
							errorMessage: `Выберите долю ${personLabel}`,
                            grid: 6,
						},
					},
					{

						field: {
							fieldType: 'checkbox',
                            label: 'Заёмщик страдает хроническими заболеваниями',
							fieldName: `borrowers[${index}].isChronical`,
                            onChange: () => touchWatch(),
						},
					},
                    {
                        isHidden: !isStaffMember,
                        field: {
                            fieldType: 'checkbox',
                            label: 'Работа заёмщика связана с риском',
                            fieldName: `borrowers[${index}].hasRisk`,
                            onChange: () => touchWatch(),
                            helpMessage: 'Например, высотные работы, работа в море, на воде или под землёй, работа с опасными химическими или взрывчатыми веществами, служба в ВС РФ, МВД или МЧС, и иная работа, связанная с повышенным риском для здоровья'
                        },
                    },
					/*{
                        isHidden: !borrowersWatch[index]?.hasRisk,
						field: {
							fieldType: 'select',
							fieldName: `borrowers[${index}].riskGroup`,
							placeholder: `Группа риска ${personLabel}`,
							errorMessage: 'Выберите группу риска',
							items: selectRiskGroup,
							isClearable: true,
                            grid: 6,
                            onChange: () => touchWatch(),
                            isDisabled: !!borrowersWatch[index]?.profession,
						},
					},
					{
                        isHidden: !borrowersWatch[index]?.hasRisk,
						field: {
							fieldType: 'select',
							fieldName: `borrowers[${index}].profession`,
							placeholder: `Профессия ${personLabel}`,
							errorMessage: 'Выберите профессию',
							items: selectProfession,
							isSearchAvailable: true,
							isClearable: true,
                            grid: 6,
                            onChange: () => touchWatch(),
                            isDisabled: !!borrowersWatch[index]?.riskGroup,
						},
					},*/
				],
			},
		};
	});

    const keepOnlyOneBank = (bankIsn: string | null) => {
        if(!bankIsn){
            return;
        }
        const wasIsDataChanged = isDataChanged;
        const newResultItems = stateResultItems.filter(item => item.isnBank === bankIsn);
        setResultItems(newResultItems);

        const bankValues = formHook.getValues('banks') || [];
        const remainingBanks: BankField[] = [];
        bankFields.forEach((field, index) => {
            const fieldBank = bankValues[index];
            if (fieldBank?.bank?.value === bankIsn) {
                remainingBanks.push(fieldBank); // Сохраняем оставшиеся банки
            }
        });
        for(let i = bankFields.length - 1; i >= 0; i--){
            bankRemove(i);
        }

        if(!wasIsDataChanged){
            setTimeout(() => {
                remainingBanks.forEach(bank => {
                    bankAppend(bank);
                });
                setIsDataChanged(false);
                const values = formHook.getValues();
                setPrecalculationFormData(JSON.parse(JSON.stringify(values)));
            }, 1);
        }
    }

    const deleteBankFromResultItems = (index: number, bankIsn: string | undefined) => {

        const bankValues = formHook.getValues('banks') || [];
        const newBankValues = bankValues.filter((_, i) => i !== index);

        for(let i = bankFields.length - 1; i >= 0; i--){
            bankRemove(i);
        }

        setTimeout(() => {
            newBankValues.forEach(bank => {
                bankAppend({
                    bank: bank?.bank,
                    rate: bank?.rate || 0,
                });
            });
        }, 1);

        if(bankIsn){
            const newResultItems = stateResultItems.filter(item => item.isnBank !== bankIsn);
            setResultItems(newResultItems);
            if(!isDataChanged){
                setTimeout(() => {
                    setIsDataChanged(false);
                    const values = formHook.getValues();
                    setPrecalculationFormData(JSON.parse(JSON.stringify(values)));
                }, 1);
            }
        }
    }

    const oneBank = bankFields.length === 1;

    const bankItems: BankItem[] = bankFields.map(({ id }, index) => {
        const showRate = true;
        return ({
            id,
            form: {
                formHook,
                fields: [
                    {
                        field: {
                            fieldName: `banks[${index}].bank`,
                            fieldType: "select",
                            items: selectBanks,
                            isLoading: loadingBanks,
                            placeholder: "Кредитная организация",
                            isRequired: true,
                            isClearable: true,
                            isSearchAvailable: true,
                            grid: 6,
                            onChange: () => setPropsChangeRnd(Math.random()),
                        }
                    },
                    {
                        isHidden: !showRate,
                        field: {
                            fieldType: 'number',
                            fieldName: `banks[${index}].rate`,
                            digitsAfterDot: 2,
                            placeholder: `Ставка кредита, %`,
                            validationType: 'minMax',
                            minValue: 0.01,
                            maxValue: 100,
                            isRequired: true,
                            errorMessage: 'Введите процентную ставку',
                            grid: oneBank ? 6 : 5,
                            onChange: () => setPropsChangeRnd(Math.random()),
                        },
                    },
                    {
                        isHidden: showRate,
                        field: {
                            fieldType: 'element',
                            fieldName: `empty${index + 1}`,
                            element: <div/>,
                            grid: oneBank ? 6 : 5,
                        }
                    },
                    {
                        isHidden: oneBank,
                        field: {
                            fieldType: 'element',
                            fieldName: `delBank${index + 1}`,
                            element: <Button appearance='icon' icon={<RedTrashIcon/>} onClick={() => {
                                const bankValues = formHook.getValues(`banks`) || [];
                                const bankIsn = bankValues[index]?.bank?.value;
                                deleteBankFromResultItems(index, bankIsn);
                            }}/>,
                            grid: 1,
                        }
                    },
                ],
            },
        });
    });

	const propertyItems: PropertyItem[] = propFields.map(({ id }, index) => {
		const defaultValues: PropertyField = propFields[index];

		const objectTypeValue = properties && properties[index] && properties[index].objectType?.value;
		const isWoodInConstruction = properties && properties[index] && properties[index].woodInConstruction;
        const objectTypeLabel = properties && properties[index] && properties[index].objectType?.label;

		return {
			id,
			form: {
				formHook,
				fields: [
					{
						field: {
							fieldType: 'select',
							fieldName: `properties[${index}].objectType`,
							isRequired: true,
							placeholder: 'Тип объекта',
							isLoading: loadingPledge,
							items: selectPledge,
							defaultValue: selectPledge[0],
							errorMessage: 'Выберите тип объекта',
							onChange: () => {
								setPropsChangeRnd(Math.random());
							},
                            grid: 6,
						},
					},
					{
						isHidden: PROPERTY_HIDE_YEAR_FOR.includes(objectTypeValue || ''),
						field: {
							fieldType: 'inputMask',
							fieldName: `properties[${index}].objectBuildYear`,
							isRequired: objectTypeValue !== PROPERTY_LAND,
							mask: '9999',
							validationType: 'minMax',
							maxValue: Number(dayjs().format('YYYY')),
                            minMaxMessage: 'Больше текущего года',
							placeholder: 'Год постройки',
							errorMessage: 'Введите год',
                            grid: 3,
						},
					},
					{
						isHidden: PROPERTY_HIDE_WOOD_ELEMENTS_FOR.includes(objectTypeValue || '') || PRECALC_MATERIAL_SHOW_FOR.includes(objectTypeValue || ''),

						field: {
							fieldType: 'checkbox',
							fieldName: `properties[${index}].woodInConstruction`,
                            label: 'В стенах/перекрытиях есть дерево',
							onChange: () => {
								setPropsChangeRnd(Math.random());
							},
                            grid: 3,
                            multiline: true,
                            width: '15.5rem'
						},
					},
					{
						isHidden: !PRECALC_MATERIAL_SHOW_FOR.includes(objectTypeValue || '') && !isWoodInConstruction,
						field: {
							fieldType: 'select',
							fieldName: `properties[${index}].wallMaterial`,
							isRequired: true,
							items: selectWallMaterial,
							isLoading: loadingWallMaterial,
							placeholder: 'Материал стен',
                            grid: 6,
						},
					},
					{
						isHidden: !PRECALC_MATERIAL_SHOW_FOR.includes(objectTypeValue || '') && !isWoodInConstruction,
						field: {
							fieldType: 'select',
							fieldName: `properties[${index}].floorMaterial`,
							isRequired: true,
							items: selectFloorMaterial,
							isLoading: loadingFloorMaterial,
							placeholder: 'Материал межэтажных перекрытий',
                            grid: 6,
						},
					},
					/*{
						field: {
							fieldType: 'number',
							fieldName: `properties[${index}].subjectCost`,
							digitsAfterDot: 2,
							placeholder: 'Рыночная стоимость объекта залога',
							isRequired: true,
							errorMessage: 'Введите стоимость',
							validationType: 'minMaxRequired',
							maxValue: 1000000000,
						},
					},*/
					{
						isHidden: (properties || []).length === 1,
						field: {
							fieldType: 'range',
							fieldName: `properties[${index}].pledgeShare`,
							placeholder: 'Доля приобретаемого / закладываемого объекта залога',
							isRequired: true,
							min: 0,
							max: 100,
							step: 1,
							defaultValue: index === 0 ? 100 : 0,
							errorMessage: 'Выберите долю',
						},
					},
				],
			},
            title: objectTypeLabel
		};
	});


	const titleForm: IForm = React.useMemo(
		() => ({
			formHook,
			fields: [
				{
					label: 'Сколько лет собственники владеют объектом?',
					field: {
						fieldType: 'radio',
						fieldName: 'ownershipYears',
						defaultValue: RADIO_TITLE[1].value,
						items: RADIO_TITLE,
					},
				},
			],
		}),
		[formHook]
	);

    const isHasSberbank = (banks || []).some(bank =>
        bank?.bank?.label.toLowerCase().includes("сбербанк")
    );

    useEffect(() => {
        if(isHasSberbank && fields.length > 1){
            for(let i = fields.length - 1; i > 0; i--){
                remove(i);
            }
        }
    }, [isHasSberbank, fields]);

	const borrowerForm: IForm = React.useMemo(
		() => ({
			formHook,
			hotReload: true,
			fields: [
				{
                    isHidden: isHasSberbank,
					field: {
						fieldType: 'checkbox',
                        label: 'Есть созаёмщики',
						fieldName: 'haveCoBorrower',
						onChange: changeHaveCoBorrowerHandler,
					},
				},
			],
		}),
		[changeHaveCoBorrowerHandler, formHook]
	);

	const risksForm: IForm = React.useMemo(
		() => ({
			formHook,
			fields: RISKS_SELECT.map<IFormField>(({ label, value }, index) => ({
				label: undefined,
				field: {
					fieldType: 'checkbox',
					fieldName: String(value),
					label,
					grid: 4,
					isDisabled: value === 'title' && !riskProperty && !riskLife,
					onChange: ({ target: { checked } }) => changeRisksHandler(checked, value),
                    width: index < 2 ? '20rem' : '10rem',
				},
			})),
		}),
		[changeRisksHandler, formHook, riskProperty, riskLife]
	);

	//const selectedBankLabels = (banks || []).map(bank => bank.label);
	/*const isCreditIssueDateRequired = (banks || []).some(bank =>
		BANKS_REQUIRED_CREDIT_DATE.some(requiredBankLabel => bank?.bank?.label?.toLowerCase().includes(requiredBankLabel))
	);*/

	const precalculationForm: IForm = React.useMemo(
		() => ({
			formHook,
			fields: [
                {
                    field: {
                        fieldType: 'number',
                        fieldName: 'summ',
                        digitsAfterDot: 2,
                        defaultValue: 5000000,
                        placeholder: 'Размер кредита',
                        validationType: 'minMax',
                        maxValue: 1000000000,
                        isRequired: true,
                        errorMessage: 'Введите размер кредита',
                        grid: 6,
                    },
                },
				{
					field: {
						fieldType: 'daData',
						daDataType: 'address',
						fieldName: 'dealCountry',
						placeholder: 'Населенный пункт сделки',
						validationType: 'addressWithCity',
						isRequired: true,
						onChange: () => {
							setTimeout(() => {
								formHook.trigger('dealCountry');
							}, 100);
						},
                        grid: 6,
					},
				},
				{
					field: {
						fieldType: 'date',
						fieldName: 'creditIssueDate',
						errorMessage: 'Выберите дату',
						placeholder: 'Дата кредитного договора',
						isRequired: true,
                        grid: 6,
					},
				},
                {
                    field: {
                        fieldType: 'element',
                        fieldName: 'hr1',
                        element: <div/>
                    }
                },
				{
					field: {
						fieldName: "insuranceType",
						fieldType: "select",
						items: insureTypes,
						placeholder: "Цель страхования",
						isRequired: true,
						defaultValue: {
							label: 'Новый кредит',
							value: '1'
						},
                        grid: 6,
					}
				},
				{
					isHidden: !isStaffMember || !agentsItems.length,
					field: {
						fieldName: "agentIsn",
						fieldType: "select",
						items: agentsItems,
						isSearchAvailable: true,
						isClearable: true,
						errorMessage: "Выберите агента",
						placeholder: "Агент",
                        grid: 6,
					},
				},
				/*{
					field: {
						fieldType: 'input',
						fieldName: 'promocode',
						placeholder: 'Промокод',
					},
				},*/
			],
		}),
		[formHook, loadingBanks, selectBanks]
	);

	const value: PrecalculationContext = React.useMemo(
		() => ({
			goBackHandler: () => navigate(-1),
			onFormSubmit,
			isLoading: loadingPrecalculation || isLoadingSaveAgreement || loadingDownloadPrecalc,
			isDisabled: isDisabledPrecalculation,
			precalculationForm,
            precalculationFormData: statePrecalculationFormData,
			borrowerForm,
			borrowersItems,
			risksForm,
			isShowRiskLife: !!riskLife,
			isShowRiskProperty: !!riskProperty,
			isShowRiskTitle: !!riskTitle,
			propertyItems,
            bankItems,
			titleForm,
			downloadAllHandler,
			isLoadingResults: loadingPrecalculation,
			changeSelectedPrecalcHandler,
			selectBankHandler,
			resultItems: stateResultItems,
			isDisabledSelectBank: !rightsHandler('createAgreement'),
			propAppend,
			propRemove,
            bankAppend,
            bankRemove,
            append,
            remove,
			formHook,
			propsChangeRnd,
            isDataChanged,
            recalcForm,
            keepOnlyOneBank,
            discountKv: stateDiscountKv,
            setDiscountKv,
            discountSk: stateDiscountSk,
            setDiscountSk,
            promocode: statePromocode,
            setPromocode,
            fixedDiscount: (stateFixedDiscountKv === "" ? 0 : stateFixedDiscountKv) + (stateFixedDiscountSk === "" ? 0 : stateFixedDiscountSk),
            stateContinueType,
            setStateContinueType,
		}),
		[
			onFormSubmit,
			stateTabs,
			isLoadingSaveAgreement,
			loadingDownloadPrecalc,
			isDisabledPrecalculation,
			precalculationForm,
			borrowerForm,
			borrowersItems,
			risksForm,
			riskLife,
			riskProperty,
			riskTitle,
			propertyItems,
			titleForm,
			downloadAllHandler,
			stateSelectedPrecalc,
			changeSelectedPrecalcHandler,
			selectBankHandler,
			stateResultItems,
			rightsHandler,
			navigate,
			properties,
			propsChangeRnd,
            stateDiscountKv,
		]
	);

	return <PrecalculationContext.Provider value={value}>{children}</PrecalculationContext.Provider>;
};

export default React.memo(PrecalculationContextProvider);
